import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Phone, LogOut, Trash2 } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";

const PersonalDetailsPage = () => {
    const navigate = useNavigate();
    const { user, api, refreshUser, logout } = useAuth();
    
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);

    useEffect(() => {
        // Parse name into first/last
        if (user?.first_name) {
            setFirstName(user.first_name);
            setLastName(user.last_name || '');
        } else if (user?.name) {
            const nameParts = user.name.split(' ');
            setFirstName(nameParts[0] || '');
            setLastName(nameParts.slice(1).join(' ') || '');
        }
        
        // Fetch history
        fetchHistory();
    }, [user]);

    const fetchHistory = async () => {
        try {
            const response = await api().get('/users/me/history');
            setHistory(response.data.history || []);
        } catch (error) {
            console.error('Failed to fetch history:', error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!firstName.trim()) {
            toast.error('First name is required');
            return;
        }

        setSaving(true);
        try {
            await api().put('/users/me', { 
                first_name: firstName.trim(),
                last_name: lastName.trim()
            });
            toast.success('Profile updated');
            if (refreshUser) await refreshUser();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        sessionStorage.removeItem('galeries_invite_code');
        navigate('/');
    };

    const handleDeleteAccount = async () => {
        try {
            await api().delete('/auth/account');
            await logout();
            sessionStorage.removeItem('galeries_invite_code');
            toast.success('Account deleted');
            navigate('/');
        } catch (error) {
            toast.error('Failed to delete account');
        }
    };

    return (
        <div className="min-h-screen bg-white pb-24">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-center py-4 relative">
                    <button
                        onClick={() => navigate('/profile')}
                        className="absolute left-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                        data-testid="back-button"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <img src={LOGO_URL} alt="Galeries Yess" className="h-8 w-auto" />
                </div>
            </div>

            <main className="pt-20 px-6 max-w-md mx-auto">
                <h1 className="text-2xl font-heading font-light tracking-tight mb-8">Personal Details</h1>

                {/* Profile Form */}
                <form onSubmit={handleSave} className="space-y-5 mb-10">
                    {/* First Name */}
                    <div>
                        <label className="text-sm font-medium text-gray-600 mb-2 block">
                            First Name
                        </label>
                        <Input
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="Your first name"
                            className="h-12"
                            data-testid="first-name-input"
                        />
                    </div>

                    {/* Last Name */}
                    <div>
                        <label className="text-sm font-medium text-gray-600 mb-2 block">
                            Last Name
                        </label>
                        <Input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Your last name"
                            className="h-12"
                            data-testid="last-name-input"
                        />
                    </div>

                    {/* Phone (read-only) */}
                    <div>
                        <label className="text-sm font-medium text-gray-600 mb-2 block">
                            Phone Number
                        </label>
                        <div className="flex items-center gap-3 h-12 px-4 bg-gray-50 border border-gray-200 rounded-md">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">{user?.phone}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Phone number cannot be changed</p>
                    </div>

                    {/* Save Button */}
                    <Button
                        type="submit"
                        disabled={saving || !firstName.trim()}
                        className="w-full h-12 bg-black hover:bg-black/90 text-white"
                        data-testid="save-details-button"
                    >
                        {saving ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            'Save Changes'
                        )}
                    </Button>
                </form>

                {/* History Section */}
                <div className="mb-10">
                    <h2 className="text-lg font-heading font-medium mb-4">History</h2>
                    
                    {loadingHistory ? (
                        <div className="py-4 text-center text-gray-400">Loading...</div>
                    ) : history.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No event history yet</p>
                    ) : (
                        <div className="space-y-4">
                            {history.map((item, index) => (
                                <div 
                                    key={item.event_id || index} 
                                    className="py-3 border-b border-gray-100"
                                    data-testid={`history-item-${index}`}
                                >
                                    <p className="font-heading font-medium text-sm mb-1">
                                        Galeries Yess — {item.season_tag || 'Event'}
                                    </p>
                                    {item.tickets_purchased > 0 && (
                                        <p className="text-sm text-gray-500">
                                            Tickets purchased ({item.tickets_purchased})
                                        </p>
                                    )}
                                    {item.donations_made > 0 && (
                                        <p className="text-sm text-gray-500">
                                            Donation made ({item.donations_made})
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="space-y-3 pt-6 border-t border-gray-100">
                    {/* Logout */}
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 py-4 text-left hover:bg-gray-50 transition-colors rounded-md px-2"
                        data-testid="logout-button"
                    >
                        <LogOut className="w-5 h-5 text-gray-400" />
                        <span className="text-sm tracking-wide">Log Out</span>
                    </button>

                    {/* Delete Account */}
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <button
                                className="w-full flex items-center gap-3 py-4 text-left hover:bg-gray-50 transition-colors rounded-md px-2"
                                data-testid="delete-account-button"
                            >
                                <Trash2 className="w-5 h-5 text-red-400" />
                                <span className="text-sm tracking-wide text-red-500">Delete Account</span>
                            </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white">
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete Account</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This action cannot be undone. This will permanently delete your account and remove all your data.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={handleDeleteAccount}
                                    className="bg-red-500 hover:bg-red-600"
                                    data-testid="confirm-delete-button"
                                >
                                    Delete
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </main>
        </div>
    );
};

export default PersonalDetailsPage;
